// src/components/forms/shared/FormElements.tsx

import {
  Box,
  BoxProps,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  SwitchProps,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { colors } from '../../../theme/theme';
import { getInputChromeStyles, getInputFocusStyles } from './InputChromeStyles';
import {
  getAccessibilityInputHover,
  getAccessibilityToggleTrackHover,
  getAccessibilityToggleThumbHover,
  InputAccessibilityHoverProps,
  ToggleAccessibilityHoverProps,
} from './AccessibilityHoverStyles';
import { useAccessibility } from '../../../contexts/AccessibilityContext';
import {
  BreakpointPadding,
  generateBreakpointMargins,
  generateBreakpointPadding,
  generateAlternatingRowStyles,
  AlternatingColorConfig,
  generateStickyMenuPositioning,
} from './MediaQueryUtils';
import { useTheme } from '@mui/material/styles';
import {
  ErrorOutline as ErrorOutlineIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const LABEL_WIDTHS = {
  default: '25%',
  xxlarge: '20%',
};

const INPUT_WIDTHS = {
  default: '75%',
  xxlarge: '80%',
};
export const FormWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  '@media (min-height: 1160px)': {
    minHeight: '1vh',
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[750]
        : theme.palette.grey[300],
  },
}));

export const FormContent = styled(Box)(({ theme }) => ({
  flex: 1,
  // paddingBottom: '2rem',
}));

export const StickyFooter = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  borderTop: `2px solid ${theme.palette.primary.light}`,
  boxShadow: '0 -4px 8px rgba(0,0,0,0.1)',
  zIndex: 2000,
  padding: '1rem 0',
  // On mobile, render inline at the end of the form instead of fixed overlay
  [theme.breakpoints.down('sm')]: {
    position: 'static',
    boxShadow: 'none',
    zIndex: 'auto',
  },

  '@media (max-height: 700px)': {
    position: 'static',
    boxShadow: 'none',
    zIndex: 'auto',
  },
}));

export const StickyButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 2rem',
});

//  StickyMenu simplified for 1400px+ (removed mobile/tablet/desktop)
export const StickyMenu = styled(Box)(({ theme }) => ({
  position: 'absolute',
  height: 'auto',
  width: 'auto',
  display: 'flex',
  border: '2px solid',
  borderColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[400]
      : theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : colors.grey[0],
  boxShadow: '0 4px 8px rgba(0,0,0,0.4)',
  zIndex: 1000,
  padding: '0',
  borderRadius: '0.5rem',
  // Apply responsive positioning - SIMPLIFIED: Only visible breakpoints (1400px+)
  // mobile, tablet, desktop removed since StickyMenu is hidden below 1400px
  ...generateStickyMenuPositioning({
    // Base styles for 1200-1399px range
    // NB. THIS SIZE IS HIDDEN BUT USED
    large: {
      top: '9.6rem',
      right: '6%',
      left: 'auto',
      padding: '0.5rem',
      width: '220px',
      maxWidth: '500px',
    },
    // VISIBLE STARTS HERE
    // MediumLarge screens (1400-1535px) - ADDITIONAL breakpoint to fix overlap at 1536x826
    mediumLarge: {
      top: '9.5rem',
      right: '2%',
      left: 'auto',
      padding: '0.6rem',
      width: '280px',
      maxWidth: '570px',
    },
    // XLarge screens (1536-2559px)
    xlarge: {
      top: '10.4rem',
      right: '5%',
      left: 'auto',
      padding: '0.25rem',
      width: '280px',
      maxWidth: '550px',
    },
    // XXLarge screens (2000px+)
    xxlarge: {
      top: '10.4rem',
      right: '30%',
      left: 'auto',
      padding: '0.5rem',
      width: '330px',
      maxWidth: '600px',
    },
  }),
}));

export const StickyMenuButtonContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '300px',
  margin: '0 auto',
  padding: '0 0 0.5rem 0',
});

export const StickyMenuFieldInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '0 0.25rem 0.5rem 1.5rem',
  color: colors.text.dark,
}));

export const StickyMenuFieldLabelContainer = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  padding: '0.25rem 0.25rem',
  '&:not(:first-of-type)': {
    borderTop: '2px solid',
    borderTopColor:
      theme.palette.mode === 'dark'
        ? colors.primary.light
        : colors.primary.main,
  },
}));

export const StickyMenuFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: 'inherit',
  '& .required': {
    color: colors.secondary.main,
    marginLeft: '4px',
  },
}));

export const DailogLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  marginTop: 0,
}));

export const DialogLabelText = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

export const FieldLabelContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['backgroundColor', 'backgroundColorMobile'].includes(prop as string),
})<{
  backgroundColor?: string;
  backgroundColorMobile?: string;
}>(({ theme, backgroundColor, backgroundColorMobile }) => ({
  width: LABEL_WIDTHS.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 1rem 0 0',
  backgroundColor:
    backgroundColor ||
    (theme.palette.mode === 'dark'
      ? theme.palette.grey[750]
      : theme.palette.grey[400]),

  [theme.breakpoints.down('md')]: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: '0.5rem 0 0.25rem 0',
    backgroundColor:
      backgroundColorMobile ||
      (theme.palette.mode === 'dark'
        ? theme.palette.grey[750]
        : theme.palette.grey[300]),
  },
  '@media (min-width: 2000px)': {
    width: LABEL_WIDTHS.xxlarge,
  },
}));

export const FieldLabelContainerMultiline = styled(Box)(({ theme }) => ({
  width: LABEL_WIDTHS.default,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  padding: '0.5rem 1rem 0 0',
  minHeight: '100%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[400],

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: '0.5rem 0 0.25rem 0',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[750]
        : theme.palette.grey[300],
  },
  '@media (max-width: 600px)': {
    minHeight: 'unset',
  },
  '@media (min-width: 2000px)': {
    width: LABEL_WIDTHS.xxlarge,
  },
}));

export const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  textAlign: 'right',
  '& .required': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[200]
        : theme.palette.secondary.main,
    marginLeft: '4px',
  },
  '& .requiredInverted': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.main
        : theme.palette.grey[200],
    marginLeft: '4px',
  },
  '& .requiredLight': {
    color: theme.palette.grey[200],
    marginLeft: '4px',
  },

  [theme.breakpoints.down('sm')]: {
    textAlign: 'left',
    paddingLeft: '1.5rem',
  },
}));

export const FieldInputContainer = styled(Box)(({ theme }) => ({
  width: INPUT_WIDTHS.default,
  padding: '0.15rem 0',
  color: colors.text.dark,

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0.15rem 1rem',
    alignSelf: 'center',
  },
  '@media (min-width: 2000px)': {
    width: INPUT_WIDTHS.xxlarge,
  },
}));

// Container for multiline field inputs (vertically stacked inputs on desktop)
export const FieldInputContainerMultiline = styled(Box)(({ theme }) => ({
  width: INPUT_WIDTHS.default,
  padding: '0.15rem 0',
  color: colors.text.dark,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  gap: '0.25rem', // Small gap between stacked inputs

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '0.15rem 1rem',
    alignSelf: 'center',
  },
  '@media (min-width: 2000px)': {
    width: INPUT_WIDTHS.xxlarge,
  },
}));

// SimpleSpacerRow elements - minimal width-only containers for use in SimpleSpacerRow component
// These maintain alignment with form field rows while allowing full styling control
export const SpacerRowLabelElement = styled(Box)(({ theme }) => ({
  width: LABEL_WIDTHS.default,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  '@media (min-width: 2000px)': {
    width: LABEL_WIDTHS.xxlarge,
  },
}));

export const SpacerRowValueElement = styled(Box)(({ theme }) => ({
  width: INPUT_WIDTHS.default,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  '@media (min-width: 2000px)': {
    width: INPUT_WIDTHS.xxlarge,
  },
}));

// Header container for field labels + icons (e.g., Pets * | EditIcon)
export const FieldInputHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  width: '100%',
  maxWidth: '800px',
}));

// Icon container for FieldInputHeader (houses price/edit icons, etc.)
export const FieldInputHeaderIconContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['border', 'padding', 'gap', 'marginRight', 'borderRadius'].includes(
      prop as string
    ),
})<{
  border?: string;
  padding?: string;
  gap?: string;
  marginRight?: string;
  borderRadius?: string;
}>(({ border, padding, gap, marginRight, borderRadius }) => ({
  display: 'flex',
  alignItems: 'center',
  border: border || 'none',
  padding: padding || '0',
  gap: gap || '0',
  marginRight: marginRight || '0',
  borderRadius: borderRadius || '0',
}));

const BaseStyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBorder',
      'accessibilityHoverBorderColor',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverGlow',
    ].includes(prop as string),
})<InputAccessibilityHoverProps & { accessibilityMode?: boolean }>(({
  theme,
  ...accessibilityProps
}) => {
  const chrome = getInputChromeStyles(theme);
  return {
    maxWidth: '800px',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-root': {
      ...chrome,
      lineHeight: 1.25,
      padding: '0.25rem',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[0],
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover': getAccessibilityInputHover(
        theme,
        Boolean(accessibilityProps.accessibilityMode),
        accessibilityProps
      ),
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused': {
        ...getInputFocusStyles(theme, chrome.boxShadow),
        ...getAccessibilityInputHover(
          theme,
          Boolean(accessibilityProps.accessibilityMode),
          accessibilityProps
        ),
      },
      '&.Mui-error': {
        border: '4px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? colors.secondary.main
            : colors.secondary.main,
      },

      /* DISABLED STATE */
      '&.Mui-disabled': {
        boxShadow: 'none',
        opacity: 0.6,
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '2rem',
      padding: '0.05rem',

      /* Remove number input spinners */
      '&::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&[type=number]': {
        MozAppearance: 'textfield',
      },
    },

    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  };
});

export const StyledTextField = React.forwardRef<HTMLDivElement, any>(
  ({ error, helperText, ...props }, ref) => {
    const theme = useTheme();
    const { accessibilityMode } = useAccessibility();

    return (
      <Box>
        <BaseStyledTextField
          ref={ref}
          error={error}
          accessibilityMode={accessibilityMode}
          {...props}
        />
        {helperText && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              mt: '0.5rem',
              ml: '0.5rem',
            }}
          >
            {error && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    border: `3px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main
                    }`,
                    backgroundColor: 'transparent',
                    opacity: 0.8,
                    animation: 'radiate 1s ease-out infinite',
                  },
                  '@keyframes radiate': {
                    '0%': {
                      transform: 'translate(-50%, -50%) scale(0.5)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) scale(2.5)',
                      opacity: 0,
                    },
                  },
                }}
              >
                <ErrorOutlineIcon
                  sx={{
                    fontSize: '2rem',
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main,
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: error ? '2rem' : '1.8rem',
                fontWeight: error ? 700 : 500,
                m: 0,
                color:
                  theme.palette.mode === 'dark'
                    ? error
                      ? theme.palette.error.light
                      : theme.palette.grey[400]
                    : error
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
              }}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

const BaseStyledMultilineTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    ![
      'accessibilityMode',
      'accessibilityHoverDisabled',
      'accessibilityHoverBorder',
      'accessibilityHoverBorderColor',
      'accessibilityHoverBackgroundColor',
      'accessibilityHoverColor',
      'accessibilityHoverGlow',
    ].includes(prop as string),
})<InputAccessibilityHoverProps & { accessibilityMode?: boolean }>(({
  theme,
  ...accessibilityProps
}) => {
  const baseBoxShadow =
    theme.palette.mode === 'dark'
      ? 'inset 0 2px 8px rgba(0, 0, 0, 0.6), inset 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 14px 2px rgba(0, 0, 0, 0.5)'
      : 'inset 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';

  return {
    maxWidth: '600px',
    // Remove the default MUI TextField border
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    // Apply solid border to the root container for multiline
    '& .MuiOutlinedInput-root': {
      border: '3px solid',
      borderColor:
        theme.palette.mode === 'dark'
          ? theme.palette.primary.main
          : theme.palette.primary.main,
      borderRadius: '0.25rem',
      boxShadow: baseBoxShadow,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[50],
      padding: 0,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover': getAccessibilityInputHover(
        theme,
        Boolean(accessibilityProps.accessibilityMode),
        accessibilityProps
      ),
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused': {
        ...getInputFocusStyles(theme, baseBoxShadow),
        ...getAccessibilityInputHover(
          theme,
          Boolean(accessibilityProps.accessibilityMode),
          accessibilityProps
        ),
      },
      '&.Mui-error': {
        border: `4px solid ${colors.secondary.main}`,
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '2rem',
      padding: '0.5rem',

      /* Remove number input spinners */
      '&::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&[type=number]': {
        MozAppearance: 'textfield',
      },
    },
    '& .MuiInputBase-root.MuiInputBase-multiline': {
      padding: 0,
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  };
});

export const StyledMultilineTextField = React.forwardRef<HTMLDivElement, any>(
  ({ error, helperText, ...props }, ref) => {
    const theme = useTheme();
    const { accessibilityMode } = useAccessibility();

    return (
      <Box>
        <BaseStyledMultilineTextField
          ref={ref}
          error={error}
          accessibilityMode={accessibilityMode}
          {...props}
        />
        {helperText && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              mt: '0.5rem',
              ml: '0.5rem',
            }}
          >
            {error && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '50%',
                    border: `3px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main
                    }`,
                    backgroundColor: 'transparent',
                    opacity: 0.8,
                    animation: 'radiate 1s ease-out infinite',
                  },
                  '@keyframes radiate': {
                    '0%': {
                      transform: 'translate(-50%, -50%) scale(0.5)',
                      opacity: 0.8,
                    },
                    '100%': {
                      transform: 'translate(-50%, -50%) scale(2.5)',
                      opacity: 0,
                    },
                  },
                }}
              >
                <ErrorOutlineIcon
                  sx={{
                    fontSize: '2rem',
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.light
                        : theme.palette.error.main,
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: error ? '2rem' : '1.8rem',
                fontWeight: error ? 700 : 500,
                m: 0,
                color:
                  theme.palette.mode === 'dark'
                    ? error
                      ? theme.palette.error.light
                      : theme.palette.grey[400]
                    : error
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
              }}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
);

interface StyledSwitchProps extends SwitchProps, ToggleAccessibilityHoverProps {
  trueLabel?: string;
  falseLabel?: string;
  showLabels?: boolean;
  labelPlacement?: 'end' | 'start' | 'both';
  labelProps?: TypographyProps;
  containerProps?: BoxProps;
}

const BaseSwitchComponent = styled(
  (props: SwitchProps) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ),
  {
    shouldForwardProp: (prop) =>
      ![
        'accessibilityMode',
        'accessibilityHoverDisabled',
        'accessibilityHoverBackgroundColor',
        'accessibilityHoverDotColor',
        'accessibilityHoverDotExpand',
      ].includes(prop as string),
  }
)<ToggleAccessibilityHoverProps & { accessibilityMode?: boolean }>(({
  theme,
  ...accessibilityProps
}) => {
  const accessibilityMode = Boolean(accessibilityProps.accessibilityMode);
  return {
    width: 60,
    height: 38,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(23px)',
        color: '#fff',
        '& .MuiSwitch-thumb': {
          backgroundColor: colors.grey[100],
          borderRight: 'none',
          marginTop: '6px',
        },
        '& + .MuiSwitch-track': {
          backgroundColor: colors.primary.main,
          opacity: 1,
          border: `3px solid ${colors.primary.main}`,
          ...theme.applyStyles('dark', {
            backgroundColor: colors.primary.dark,
            border: `3px solid ${colors.primary.light}`,
          }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: colors.primary.light,
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: colors.grey[100],
        ...theme.applyStyles('dark', {
          color: colors.grey[600],
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.7,
        ...theme.applyStyles('dark', {
          opacity: 0.3,
        }),
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
      marginTop: '6px',
      marginLeft: '6px',
      backgroundColor: colors.grey[900],
      ...theme.applyStyles('dark', {
        backgroundColor: colors.grey[400],
      }),
    },
    '&& .MuiSwitch-track': {
      borderRadius: 40 / 2,
      border: `3px solid ${colors.grey[900]}`,
      backgroundColor: colors.grey[100],
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
      ...theme.applyStyles('dark', {
        backgroundColor: colors.grey[800],
        border: `3px solid ${colors.grey[300]}`,
      }),
    },
    '&:hover .MuiSwitch-track': getAccessibilityToggleTrackHover(
      theme,
      accessibilityMode,
      accessibilityProps
    ),
    '&:hover .MuiSwitch-thumb': getAccessibilityToggleThumbHover(
      accessibilityMode,
      accessibilityProps
    ),
  };
});

export const StyledSwitch: React.FC<StyledSwitchProps> = ({
  trueLabel = 'Yes',
  falseLabel = 'No',
  showLabels = false,
  labelPlacement = 'end',
  labelProps = {},
  containerProps = {},
  checked,
  onChange,
  disabled,
  accessibilityHoverDisabled,
  accessibilityHoverBackgroundColor,
  accessibilityHoverDotColor,
  accessibilityHoverDotExpand,
  ...switchProps
}) => {
  const theme = useTheme();
  const { accessibilityMode } = useAccessibility();
  const accessibilityHoverProps = {
    accessibilityHoverDisabled,
    accessibilityHoverBackgroundColor,
    accessibilityHoverDotColor,
    accessibilityHoverDotExpand,
  };

  if (!showLabels) {
    return (
      <BaseSwitchComponent
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        accessibilityMode={accessibilityMode}
        {...accessibilityHoverProps}
        {...switchProps}
      />
    );
  }

  const handleContainerClick = (event: React.MouseEvent<HTMLElement>) => {
    const isClickOnSwitch = (event.target as HTMLElement).closest(
      '.MuiSwitch-root'
    );
    if (!isClickOnSwitch && onChange && !disabled) {
      const syntheticEvent = {
        target: { checked: !checked },
        currentTarget: { checked: !checked },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent, !checked);
    }
  };

  const defaultLabelProps: TypographyProps = {
    sx: {
      fontSize: '2rem',
      cursor: disabled ? 'default' : 'pointer',
      userSelect: 'none',
      color:
        theme.palette.mode === 'dark' ? theme.palette.grey[200] : 'inherit',
      ...labelProps.sx,
    },
    ...labelProps,
  };

  const currentLabel = checked ? trueLabel : falseLabel;

  const renderLabel = () => (
    <Typography {...defaultLabelProps}>{currentLabel}</Typography>
  );

  // Hit area includes the label - hovering anywhere in this container
  // reaches the switch's track/thumb via descendant selectors below.
  const containerStyling: BoxProps['sx'] = {
    display: 'flex',
    alignItems: 'center',
    cursor: disabled ? 'default' : 'pointer',
    WebkitTapHighlightColor: 'transparent',
    '&:active': {
      backgroundColor: 'transparent',
    },
    '&:hover .MuiSwitch-track': getAccessibilityToggleTrackHover(
      theme,
      accessibilityMode,
      accessibilityHoverProps
    ),
    '&:hover .MuiSwitch-thumb': getAccessibilityToggleThumbHover(
      accessibilityMode,
      accessibilityHoverProps
    ),
    ...containerProps.sx,
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={containerStyling}
      {...containerProps}
    >
      {(labelPlacement === 'start' || labelPlacement === 'both') && (
        <Box sx={{ mr: 1 }}>{renderLabel()}</Box>
      )}

      <BaseSwitchComponent
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        accessibilityMode={accessibilityMode}
        {...accessibilityHoverProps}
        {...switchProps}
      />

      {(labelPlacement === 'end' || labelPlacement === 'both') && (
        <Box sx={{ ml: 1 }}>{renderLabel()}</Box>
      )}
    </Box>
  );
};

// Standard form row container with responsive flexDirection
export const FormFieldRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',

  // Mobile: stack vertically
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginTop: 0,
    marginBottom: '0.25rem',
  },
}));

// FormFieldRowMultiline: Single logical row that visually appears as multiple rows
// Used for paired inputs like check-in/check-out dates in a single FormFieldRow
export const FormFieldRowMultiline = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  alignItems: 'stretch',
  minHeight: '100%',

  // Mobile: stack vertically
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginTop: 0,
    marginBottom: '0.25rem',
  },
}));

// SimpleSpacerRow component for creating vertical spacing in forms
interface SpacerRowProps {
  height?: string;
  mobileHeight?: string; // Optional height override for mobile
  border?: string; // Custom border style, takes precedence over borderBottom
  borderBottom?: boolean | string; // true for default divider, string for custom border
  backgroundColor?: string; // Optional background color override for desktop
  backgroundColorMobile?: string; // Optional background color override for mobile
}

export const SimpleSpacerRow: React.FC<SpacerRowProps> = ({
  height = '1rem',
  mobileHeight,
  border,
  borderBottom = false,
  backgroundColor,
  backgroundColorMobile,
}) => {
  const shouldShowBorder = border || borderBottom;
  const getBorderBottomStyle = () => {
    if (!borderBottom) return undefined;
    if (typeof borderBottom === 'string') return borderBottom;
    return '1px solid'; // default border style
  };
  return (
    <FormFieldRow
      sx={{
        height,
        ...(mobileHeight && {
          '@media (max-width: 600px)': {
            height: mobileHeight,
          },
        }),
      }}
    >
      <FieldLabelContainer
        backgroundColor={backgroundColor}
        backgroundColorMobile={backgroundColorMobile}
      />
      <FieldInputContainer>
        {shouldShowBorder && (
          <Box
            sx={{
              ...(border
                ? { border }
                : {
                    borderBottom: getBorderBottomStyle(),
                    borderColor: 'dividerContrast',
                  }),
              width: '100%',
              maxWidth: '800px',
            }}
          />
        )}
      </FieldInputContainer>
    </FormFieldRow>
  );
};

interface BottomSpacerRowProps {
  datesSelected?: boolean;
  minHeight?: string;
}

export const BottomSpacerRow: React.FC<BottomSpacerRowProps> = ({
  datesSelected,
  minHeight = '14rem',
}) => {
  return (
    <FormFieldRow
      sx={(theme) => ({
        flex: 1,
        minHeight,
        display: 'flex',

        [theme.breakpoints.down('sm')]: {
          minHeight: '1.5rem',
        },

        '@media (max-height: 700px)': {
          minHeight: '5.5rem',
        },
      })}
    >
      <FieldLabelContainer />
      <FieldInputContainer />
    </FormFieldRow>
  );
};

interface TopSpacerRowProps {
  datesSelected?: boolean;
}

export const TopSpacerRow: React.FC<TopSpacerRowProps> = ({
  datesSelected = false,
}) => {
  return (
    <FormFieldRow
      sx={{
        flex: 1,
        minHeight: datesSelected ? '0.5rem' : '1.5rem',
        display: 'none',
        '@media (min-width: 600px)': {
          display: 'flex',
        },
      }}
    >
      <FieldLabelContainer />
      <FieldInputContainer />
    </FormFieldRow>
  );
};

// ResponsiveFormGrid with theme-aware alternating colors
export interface ResponsiveFormGridProps {
  children: React.ReactNode;
  container?: boolean;
  spacing?: number;
  className?: string;
  breakpointPadding?: BreakpointPadding;
  alternateRowColors?: boolean;
  alternatingColors?: AlternatingColorConfig;
  rowPadding?: string;
}

export const ResponsiveFormGrid = styled(Grid, {
  shouldForwardProp: (prop) =>
    ![
      'alternateRowColors',
      'alternatingColors',
      'breakpointPadding',
      'rowPadding',
    ].includes(prop as string),
})<ResponsiveFormGridProps>(({
  theme,
  alternateRowColors = false,
  alternatingColors = {},
  breakpointPadding = {},
  rowPadding,
}) => {
  const finalPadding = rowPadding
    ? { default: rowPadding, medium: rowPadding, large: rowPadding }
    : breakpointPadding;

  const marginStyles = generateBreakpointMargins();
  const paddingStyles = generateBreakpointPadding(finalPadding);

  const alternatingStyles = generateAlternatingRowStyles(
    alternateRowColors,
    alternatingColors,
    theme
  );

  return {
    '& .MuiGrid-item': {
      ...marginStyles,
      boxSizing: 'border-box',
    },

    ...(alternateRowColors && {
      '& > .MuiGrid-item': {
        ...paddingStyles,
      },
    }),

    ...alternatingStyles,
  };
});

// Export types
export interface FieldLabelProps {
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}

export interface FormFieldRowProps {
  children: React.ReactNode;
  className?: string;
}

export const createResponsiveFormStyles = (
  breakpointPadding: BreakpointPadding = {},
  alternateRowColors: boolean = false,
  alternatingColors: AlternatingColorConfig = {},
  theme?: any
) => {
  const marginStyles = generateBreakpointMargins();
  const paddingStyles = generateBreakpointPadding(breakpointPadding);

  const alternatingStyles = generateAlternatingRowStyles(
    alternateRowColors,
    alternatingColors,
    theme
  );

  return {
    '& .MuiGrid-item': marginStyles,
    ...(alternateRowColors && {
      '& > .MuiGrid-item': paddingStyles,
    }),
    ...alternatingStyles,
  };
};

export const useResponsiveFormStyles = (
  breakpointPadding: BreakpointPadding = {},
  alternateRowColors: boolean = false,
  alternatingColors: AlternatingColorConfig = {},
  theme?: any
) => {
  return {
    sx: createResponsiveFormStyles(
      breakpointPadding,
      alternateRowColors,
      alternatingColors,
      theme
    ),
  };
};

// ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••
// Form Page Header Styled Components (extracted from BeingsPage)
// ••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••

export const FormPageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  borderBottom: `3px solid ${theme.palette.primary.dark}`,
  backgroundColor: theme.palette.primary.main,
  position: 'sticky',
  top: '64px',
  zIndex: '1100',
}));

export const FormPageTitleBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
  height: '5rem',
  padding: '0 0 0 0.1rem',
  whiteSpace: 'nowrap',
  position: 'relative',

  // MOBILE: Enable horizontal scrolling
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  paddingRight: '4rem',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  // Tablet and above
  '@media (min-width: 600px)': {
    gap: '15px',
    padding: '0 0 0 0.1rem',
  },

  // Media query for medium laptops
  '@media (max-width: 1540px)': {
    height: '4rem',
  },
});

export const FormPageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'none',
  letterSpacing: '0.5px',
}));

export const FormPageModeText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'none',
  letterSpacing: '0.5px',
}));

export const FormPageModeSeparator = styled('span')({
  color: '#ffffff',
  fontSize: '2rem',
  fontWeight: 700,
  margin: '0',
});

// Section header component (blue background, white text)
// Used to create a visual section header for lists like Pets, Kennels, Surcharges
export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: INPUT_WIDTHS.default,
  maxWidth: '800px',
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#ffffff',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.main,
  padding: '0.075rem 0 0.2rem 1rem',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: 'none',
    padding: '0.25rem 0 0.25rem 1.5rem',
  },

  '@media (min-width: 2000px)': {
    width: INPUT_WIDTHS.xxlarge,
  },

  '& *': {
    color: '#ffffff !important',
  },
}));

// Wrapper for section header on desktop (for alignment)
export const SectionHeaderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    // marginBottom: '0.25rem',
  },
}));

// Compact number input with custom up/down buttons for table cells
const InlineNumberInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const InlineNumberInputRoot = styled(TextField)(({ theme }) => ({
  width: '160px',
  '& .MuiInputBase-root': {
    height: '60px',
    fontSize: '2.6rem',
    fontWeight: 600,
    backgroundColor: theme.palette.background.paper,
    paddingRight: 0,
  },
  '& .MuiInputBase-input': {
    padding: '4px 8px',
    textAlign: 'right',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&[type=number]': {
      MozAppearance: 'textfield',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
  },
}));

const ClearBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.secondary.main,
  border: 'none',
  borderRadius: '50%',
  padding: '.6rem',
  marginRight: '1.2rem',
  color: theme.palette.grey[50],
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '& svg': { fontSize: '1.8rem' },
}));

const SpinnerStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  borderLeft: '2px solid',
  borderColor: 'inherit',
  marginRight: '-1px',
});

const SpinnerBtn = styled(IconButton)(({ theme }) => ({
  width: '40px',
  height: '30px',
  borderRadius: 0,
  color: theme.palette.primary.main,
  padding: 0,
  '& svg': { fontSize: '1.4rem' },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& + &': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

interface InlineNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onClear?: () => void;
  step?: number;
  min?: number;
  autoFocus?: boolean;
}

export const InlineNumberInput: React.FC<InlineNumberInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onClear,
  step = 5,
  min = 0,
  autoFocus,
}) => {
  const handleStep = (direction: 1 | -1) => (e: React.MouseEvent) => {
    e.preventDefault();
    const num = parseFloat(value) || 0;
    const newVal = num + step * direction;
    if (newVal >= min) onChange(String(newVal));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    onClear?.();
  };

  const noLeftRadius = onClear
    ? {
        '& .MuiOutlinedInput-root': {
          borderRadius: '0 4px 4px 0',
        },
      }
    : undefined;

  return (
    <InlineNumberInputWrapper>
      {onClear && (
        <ClearBox onMouseDown={handleClear} tabIndex={-1}>
          <CloseIcon />
        </ClearBox>
      )}
      <InlineNumberInputRoot
        autoFocus={autoFocus}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        size="small"
        sx={noLeftRadius}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0 }}>
              <SpinnerStack>
                <SpinnerBtn onMouseDown={handleStep(1)} tabIndex={-1}>
                  <ArrowDropUpIcon />
                </SpinnerBtn>
                <SpinnerBtn onMouseDown={handleStep(-1)} tabIndex={-1}>
                  <ArrowDropDownIcon />
                </SpinnerBtn>
              </SpinnerStack>
            </InputAdornment>
          ),
        }}
      />
    </InlineNumberInputWrapper>
  );
};

export { FORM_MEDIA_QUERIES } from './MediaQueryUtils';
